'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { useCustomer } from './customer-context';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

export default function UserMenu() {
  const { customer, isAuthenticated, isLoading, logout } = useCustomer();
  const [isMobile, setIsMobile] = useState(false);
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Close menu when navigating
  useEffect(() => {
    const menu = menuRef.current;
    if (menu) {
      const button = menu.querySelector('button');
      if (button && button.getAttribute('aria-expanded') === 'true') {
        button.click();
      }
    }
  }, [pathname]);

  if (isLoading) {
    return (
      <div className="h-9 w-9 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800"></div>
    );
  }

  if (!isAuthenticated || !customer) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
  };

  const displayName = customer.firstName || customer.email?.split('@')[0] || 'Account';

  return (
    <Menu as="div" className="relative inline-block text-left" ref={menuRef}>
      {({ open }) => (
        <>
          <Menu.Button
            className={`flex items-center gap-1 rounded-md border border-neutral-700 px-3 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 ${
              open ? 'bg-neutral-800' : ''
            }`}
          >
            <UserCircleIcon className="h-4 w-4" />
            <span className="max-w-[100px] truncate">{displayName}</span>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-neutral-700 rounded-md bg-[#1A1A1D] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="px-4 py-3">
                <p className="text-sm text-neutral-400">Signed in as</p>
                <p className="truncate text-sm font-medium text-white">{customer.firstName || customer.displayName || 'Account'}</p>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      href="/account"
                      className={`block px-4 py-2 text-sm text-white ${
                        active ? 'bg-neutral-800' : ''
                      }`}
                    >
                      Account Settings
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      href="/account/orders"
                      className={`block px-4 py-2 text-sm text-white ${
                        active ? 'bg-neutral-800' : ''
                      }`}
                    >
                      Orders
                    </Link>
                  )}
                </Menu.Item>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={`block w-full px-4 py-2 text-left text-sm text-red-500 ${
                        active ? 'bg-neutral-800' : ''
                      }`}
                    >
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}
